import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  CheckCircle,
  Building2,
  User,
  MessageSquare,
  Package
} from 'lucide-react';
import { Button } from '../components/ui/button';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const [submitError, setSubmitError] = useState('');

  const productOptions = [
    { value: '', label: 'Select a product category' },
    { value: 'small-format', label: 'Small Format (Business Cards, Flyers, Brochures)' },
    { value: 'large-format', label: 'Large Format (Banners, Signs, Displays)' },
    { value: 'awards', label: 'Awards & Recognition (Trophies, Plaques, Crystal)' },
    { value: 'promo-swag', label: 'Promotional Swag (Branded Merchandise)' },
    { value: 'custom-project', label: 'Custom Project / Multiple Categories' },
    { value: 'other', label: 'Other / General Inquiry' },
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !/^[\d\s\-\+\(\)]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company || null,
          product_interest: formData.productInterest || null,
          message: formData.message
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit form');
      }
      
      setIsSubmitted(true);
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        productInterest: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitError(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '+1 800 521 0527',
      href: 'tel:+18005210527',
      description: 'Mon-Fri 8am-5pm PST'
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'sales@engraveca.com',
      href: 'mailto:sales@engraveca.com',
      description: 'We respond within 24 hours'
    },
    {
      icon: MapPin,
      title: 'Address',
      value: '1489 East Warner Avenue',
      secondLine: 'Santa Ana, CA 92705',
      href: 'https://maps.google.com/?q=1489+East+Warner+Avenue+Santa+Ana+CA+92705',
      description: 'Visit our showroom'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      value: 'Monday - Friday',
      secondLine: '8:00 AM - 5:00 PM PST',
      description: 'Closed weekends & holidays'
    }
  ];

  return (
    <div className="min-h-screen bg-ivory-50" data-testid="contact-page">
      {/* Header */}
      <div className="relative gradient-hero py-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/5 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-6 transition-colors font-body"
            data-testid="contact-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gold-500/10 flex items-center justify-center">
              <MessageSquare className="w-7 h-7 text-gold-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800">
              Contact Us
            </h1>
          </div>
          <p className="text-ivory-500 font-body max-w-2xl">
            Have a question or ready to start your project? We&apos;d love to hear from you. 
            Fill out the form below or reach out directly.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-ivory-100 rounded-2xl border border-ivory-300/50 p-8 shadow-luxury">
                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">
                      Message Sent Successfully!
                    </h2>
                    <p className="text-ivory-500 font-body mb-8 max-w-md mx-auto">
                      Thank you for reaching out. Our team will review your inquiry and get back to you within 24 hours.
                    </p>
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      className="btn-luxury-primary rounded-lg font-body"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-2">
                      Send Us a Message
                    </h2>
                    <p className="text-ivory-500 font-body mb-8">
                      Fill out the form and we&apos;ll get back to you as soon as possible.
                    </p>
                    
                    {/* Error Alert */}
                    {submitError && (
                      <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                        <p className="text-red-600 font-body text-sm">{submitError}</p>
                      </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      {/* Name & Email Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-ivory-700 mb-2 font-body">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ivory-400" />
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="John Smith"
                              className={`w-full pl-11 pr-4 py-3 rounded-lg border ${
                                errors.name ? 'border-red-400 focus:ring-red-500' : 'border-ivory-300 focus:ring-gold-500'
                              } bg-ivory-50 text-ivory-800 placeholder-ivory-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all font-body`}
                              data-testid="contact-name-input"
                            />
                          </div>
                          {errors.name && <p className="text-red-500 text-sm mt-1 font-body">{errors.name}</p>}
                        </div>
                        
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-ivory-700 mb-2 font-body">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ivory-400" />
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="john@company.com"
                              className={`w-full pl-11 pr-4 py-3 rounded-lg border ${
                                errors.email ? 'border-red-400 focus:ring-red-500' : 'border-ivory-300 focus:ring-gold-500'
                              } bg-ivory-50 text-ivory-800 placeholder-ivory-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all font-body`}
                              data-testid="contact-email-input"
                            />
                          </div>
                          {errors.email && <p className="text-red-500 text-sm mt-1 font-body">{errors.email}</p>}
                        </div>
                      </div>
                      
                      {/* Phone & Company Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-ivory-700 mb-2 font-body">
                            Phone Number
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ivory-400" />
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="(555) 123-4567"
                              className={`w-full pl-11 pr-4 py-3 rounded-lg border ${
                                errors.phone ? 'border-red-400 focus:ring-red-500' : 'border-ivory-300 focus:ring-gold-500'
                              } bg-ivory-50 text-ivory-800 placeholder-ivory-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all font-body`}
                              data-testid="contact-phone-input"
                            />
                          </div>
                          {errors.phone && <p className="text-red-500 text-sm mt-1 font-body">{errors.phone}</p>}
                        </div>
                        
                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-ivory-700 mb-2 font-body">
                            Company Name
                          </label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ivory-400" />
                            <input
                              type="text"
                              id="company"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              placeholder="Your Company"
                              className="w-full pl-11 pr-4 py-3 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 placeholder-ivory-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all font-body"
                              data-testid="contact-company-input"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Product Interest */}
                      <div>
                        <label htmlFor="productInterest" className="block text-sm font-medium text-ivory-700 mb-2 font-body">
                          Product Interest
                        </label>
                        <div className="relative">
                          <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ivory-400" />
                          <select
                            id="productInterest"
                            name="productInterest"
                            value={formData.productInterest}
                            onChange={handleChange}
                            className="w-full pl-11 pr-4 py-3 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all font-body appearance-none cursor-pointer"
                            data-testid="contact-product-select"
                          >
                            {productOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-5 h-5 text-ivory-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      {/* Message */}
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-ivory-700 mb-2 font-body">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          placeholder="Tell us about your project, quantities needed, timeline, and any special requirements..."
                          className={`w-full px-4 py-3 rounded-lg border ${
                            errors.message ? 'border-red-400 focus:ring-red-500' : 'border-ivory-300 focus:ring-gold-500'
                          } bg-ivory-50 text-ivory-800 placeholder-ivory-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all font-body resize-none`}
                          data-testid="contact-message-input"
                        />
                        {errors.message && <p className="text-red-500 text-sm mt-1 font-body">{errors.message}</p>}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 btn-luxury-primary rounded-lg font-body font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <Send className="w-5 h-5" />
                            Send Message
                          </span>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
            
            {/* Contact Information Sidebar */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                <h2 className="text-xl font-heading font-semibold text-ivory-800">
                  Get in Touch
                </h2>
                
                {contactInfo.map((info, index) => (
                  <div 
                    key={index}
                    className="p-5 rounded-xl bg-ivory-100 border border-ivory-300/50 shadow-luxury hover:shadow-luxury-lg transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-gold-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-ivory-800 mb-1 font-body">{info.title}</h3>
                        {info.href ? (
                          <a 
                            href={info.href}
                            target={info.href.startsWith('http') ? '_blank' : undefined}
                            rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="text-gold-600 hover:text-gold-700 transition-colors font-body"
                          >
                            {info.value}
                            {info.secondLine && <><br />{info.secondLine}</>}
                          </a>
                        ) : (
                          <p className="text-ivory-700 font-body">
                            {info.value}
                            {info.secondLine && <><br />{info.secondLine}</>}
                          </p>
                        )}
                        <p className="text-ivory-500 text-sm mt-1 font-body">{info.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Quick Links */}
                <div className="p-5 rounded-xl gradient-champagne border border-gold-500/20">
                  <h3 className="font-medium text-ivory-800 mb-3 font-body">Quick Links</h3>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/faq" className="text-gold-600 hover:text-gold-700 text-sm font-body flex items-center gap-2">
                        <span>→</span> Frequently Asked Questions
                      </Link>
                    </li>
                    <li>
                      <Link to="/products" className="text-gold-600 hover:text-gold-700 text-sm font-body flex items-center gap-2">
                        <span>→</span> Browse Products
                      </Link>
                    </li>
                    <li>
                      <Link to="/Awards" className="text-gold-600 hover:text-gold-700 text-sm font-body flex items-center gap-2">
                        <span>→</span> Awards & Recognition
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
